import React, { useLayoutEffect, useRef, useState } from "react";
import {
  CHAMBER_GLOBALS_REQUIRED_MESSAGE,
  MULTIPLE_BILLING_DATES_REQUIRED_MESSAGE,
  RENEWAL_FIXED_DATE_REQUIRED_MESSAGE,
  getMissingChamberGlobalsFields,
} from "../chamberGlobalsValidation";
import { Checkbox, Radio, Switch } from "@mui/material";
import { inlineChoiceControlSx } from "../../../../common/dashboard/formStyles";
import GoogleAddressTextField from "../../../../common/GoogleAddressTextField";
import CountrySelectField from "../../../../common/dashboard/CountrySelectField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { faCalendar } from "@fortawesome/free-regular-svg-icons";
import {
  ChamberMemberProEventTicketCreditCardPaymentModeEnum,
  ChamberMemberProInvoicePaymentTermsEnum,
  ChamberMemberProManualPaymentMethodTypeEnum,
  ChamberMemberProStripeConnectModeEnum,
  ChamberMemberProStripeConnectStatusEnum,
} from "../../../../../graphql/types";
import { getCmpCreditCardPaymentsReady } from "../../../../../utils/cmpCreditCardPayments";
import { applyCmpOrganizationNoun } from "../../../../../utils/cmpOrganizationNoun";
import { useCmpOrganizationNoun } from "../../../../../contexts/CmpOrganizationNounContext";
import {
  CalendarDateInput,
  DecimalNumberInput,
  FieldLabel,
  GlobalSettingsSectionId,
  MoneyInput,
  MonthDayCalendarValue,
  SectionSaveReset,
  SectionSaveResetProps,
  SettingsModal,
  TextInput,
  WholeNumberInput,
  calendarMonthOptions,
  settingsPrimaryButtonClassName,
  settingsSectionCardClassName,
  settingsSectionDescriptionClassName,
  settingsSectionTitleClassName,
  settingsSecondaryButtonClassName,
  switchSx,
} from "../common";
import EmailDomainSettingsSection from "../sections/EmailDomainSettingsSection";
import {
  ChamberSettingsFormState,
  PaymentMethod,
  defaultMultipleBillingDatesSettings,
  paymentMethods,
} from "../settingsData";
import {
  ChamberGroupInsuranceBillingErrors,
  getChamberGroupInsuranceBillingErrors,
} from "../chamberGroupInsuranceValidation";
import { normalizeInvoiceStartNumber } from "../invoiceStartNumber";
import { getInvoiceFooterNotesText } from "../invoiceFooterNotes";
import {
  InvoiceBillingContactFields,
  globalSettingsDesignInputClassName,
} from "./GlobalSettingsPendingFields";
import { STAFF_SUPPORT_EMAIL } from "../../../../../utils/userFacingError";
import { formatStaffPhoneInput } from "../../../../../utils/memberContactFormatting";

type GlobalSettingsTabProps = {
  settings: ChamberSettingsFormState;
  // The chamber's own contact email. It prefills blank invoice copy
  // addresses when staff edit Invoicing Options.
  chamberContactEmail?: string;
  // The chamber's CMP website address. Invoice Footer Notes links to its
  // Cancellation and Refund Policy page.
  chamberWebsiteUrl?: string | null;
  // Opens the Automated Emails tab. Absent when the user cannot see that tab.
  onOpenAutomatedEmails?: () => void;
  isSandboxChamber: boolean;
  onChange: (settings: ChamberSettingsFormState) => void;
  sectionControls: Record<GlobalSettingsSectionId, SectionSaveResetProps>;
  onStartStripeConnect: () => void;
  onRefreshStripeConnect: () => void;
  onDeleteStripeConnect: () => void;
  stripeConnectActionLoading: boolean;
  stripeConnectRefreshLoading: boolean;
  stripeConnectDeleteLoading: boolean;
  stripeConnectError: string | null;
  onStartSenderDomainVerification: (values: {
    domain: string;
    senderEmail: string;
  }) => Promise<void>;
  onRefreshSenderDomainStatus: () => Promise<void>;
  onRemoveSenderDomain: () => Promise<void>;
  onSendSenderDomainTestEmail: (recipientEmail: string) => Promise<void>;
  senderDomainActionLoading: boolean;
  senderDomainRefreshLoading: boolean;
  senderDomainRemoveLoading: boolean;
  senderDomainTestEmailLoading: boolean;
  senderDomainError: string | null;
  hasUnsavedSettingsChanges: boolean;
};

const northAmericanTimeZones = [
  "(UTC-10:00) Hawaii-Aleutian Time",
  "(UTC-09:00) Alaska Time",
  "(UTC-08:00) Pacific Time",
  "(UTC-07:00) Mountain Time",
  "(UTC-07:00) Arizona Time",
  "(UTC-06:00) Central Time",
  "(UTC-06:00) Saskatchewan Time",
  "(UTC-05:00) Eastern Time",
  "(UTC-05:00) Indiana Eastern Time",
  "(UTC-04:00) Atlantic Time",
  "(UTC-03:30) Newfoundland Time",
];

// B13 verbatim fix (screenshot 8.57.45 shows "…Eastern Time (US & C…"): the
// mock's standard-zone entries carry the conventional "(US & Canada)" suffix.
// DISPLAY-ONLY — the option VALUES above are the stored settings strings and
// are the lookup keys in src/utils/cmpTimeZones.ts, so only the visible label
// gains the suffix; the saved value is unchanged.
const timeZoneDisplayLabelByValue: Record<string, string> = {
  "(UTC-08:00) Pacific Time": "(UTC-08:00) Pacific Time (US & Canada)",
  "(UTC-07:00) Mountain Time": "(UTC-07:00) Mountain Time (US & Canada)",
  "(UTC-06:00) Central Time": "(UTC-06:00) Central Time (US & Canada)",
  "(UTC-05:00) Eastern Time": "(UTC-05:00) Eastern Time (US & Canada)",
};

const getMonthDayValue = (month: string, day: string) => `${month}|${day}`;

const monthIndexByLabel = calendarMonthOptions.reduce(
  (acc, month, index) => ({
    ...acc,
    [month.label]: index,
  }),
  {} as Record<string, number>
);

const parseMonthDayValue = (monthDayValue: string) => {
  const [month, day] = monthDayValue.split("|");

  return {
    month: month || calendarMonthOptions[0].label,
    day: day || "1",
  };
};

const getFiscalYearEndLabel = (monthDayValue: string) => {
  const { month, day } = parseMonthDayValue(monthDayValue);
  const monthIndex = monthIndexByLabel[month] || 0;
  const parsedDay = Number(day) || 1;
  const startDate = new Date(2025, monthIndex, parsedDay);
  const endDate = new Date(
    startDate.getFullYear() + 1,
    startDate.getMonth(),
    startDate.getDate()
  );
  endDate.setDate(endDate.getDate() - 1);

  return `${calendarMonthOptions[endDate.getMonth()].label} ${endDate.getDate()}`;
};

const chamberGlobalInputClassName =
  "h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-base text-gray-900 shadow-sm outline-none transition focus:border-cpaGold focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-500";

const chamberGlobalSelectClassName = `${chamberGlobalInputClassName} appearance-none pr-12`;

const GlobalSelectInput = ({
  value,
  onChange,
  options,
  ariaLabel,
  icon,
  error = false,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
  icon: "calendar" | "chevron";
  error?: boolean;
}) => {
  const selectOptions = options.some((option) => option.value === value)
    ? options
    : [{ value, label: value }, ...options];

  return (
    <div className="relative">
      <select
        aria-label={ariaLabel}
        aria-invalid={error || undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`${chamberGlobalSelectClassName}${error ? " !border-[#F21B1B]" : ""}`}
      >
        {selectOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <FontAwesomeIcon
        icon={icon === "calendar" ? faCalendar : faChevronDown}
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
      />
    </div>
  );
};

const GlobalReadOnlyDateInput = ({
  value,
  ariaLabel,
}: {
  value: string;
  ariaLabel: string;
}) => (
  <div className="relative">
    <input
      aria-label={ariaLabel}
      value={value}
      readOnly
      className={`${chamberGlobalInputClassName} cursor-default pr-12`}
    />
    <FontAwesomeIcon
      icon={faCalendar}
      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
    />
  </div>
);

const ChamberGlobalField = ({
  title,
  helper,
  children,
  headerClassName = "",
  required = false,
}: {
  title: string;
  helper: string;
  children: React.ReactNode;
  headerClassName?: string;
  required?: boolean;
}) => (
  <div>
    <div className={headerClassName}>
      <h3 className="text-base font-semibold text-gray-950">
        {title}
        {required ? <span className="text-[#E24C4C]"> *</span> : null}
      </h3>
      <p className="mt-2 text-sm leading-6 text-black">
        {helper}
      </p>
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

const EnabledSwitchControl = ({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}) => (
  <div className="flex shrink-0 items-center gap-3">
    <Switch
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      sx={switchSx}
      inputProps={{ "aria-label": ariaLabel }}
    />
    <span
      className={`text-xs font-semibold uppercase ${
        checked ? "text-emerald-600" : "text-gray-500"
      }`}
    >
      {checked ? "Enabled" : "Disabled"}
    </span>
  </div>
);

const setupSectionInputClassName =
  "h-12 w-full rounded-lg border border-gray-200 bg-white px-4 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-cpaGold focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500";

const screenshotSectionClassName =
  "bg-white px-6 py-4 shadow-none";

const ScreenshotSection = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section className={settingsSectionCardClassName}>
    <div className="px-0">
      <h2 className={`whitespace-pre-wrap ${settingsSectionTitleClassName}`}>
        {title}
      </h2>
      {description && (
        <p className={settingsSectionDescriptionClassName}>{description}</p>
      )}
    </div>
    <div className="mt-7">{children}</div>
  </section>
);

const invoiceTermOptions = [
  {
    label: "Due Upon Receipt",
    value: ChamberMemberProInvoicePaymentTermsEnum.DueUponReceipt,
  },
  {
    label: "30 days from invoice date",
    value: ChamberMemberProInvoicePaymentTermsEnum.Net_30,
  },
  {
    label: "45 days from invoice date",
    value: ChamberMemberProInvoicePaymentTermsEnum.Net_45,
  },
];

type MembershipApplicationCommunicationOptionKey =
  keyof ChamberSettingsFormState["setup"]["membershipApplicationCommunicationOptions"];

const membershipApplicationCommunicationOptionRows: Array<{
  key: MembershipApplicationCommunicationOptionKey;
  label: string;
}> = [
  {
    key: "emailEnabled",
    label:
      "I prefer communication from the chamber to be done via email. Member is aware they can unsubscribe at any time.",
  },
  {
    key: "phoneEnabled",
    label: "I prefer communication from the chamber to be done via phone.",
  },
];

// Design D:5878-5881: the text-message option is not available yet.
const textMessageCommunicationOptionLabel =
  "I prefer communication from the chamber to be done via text message.";

// The "Company Cheque" card is the only control for two saved methods:
// CHEQUE (the Join Now option) and INVOICE_ME (the default manual method for
// staff event invoices and renewal invoices). The backend shows both as one
// public "Company Cheque" option. The card switches both together and shows
// ON only when every saved one is on: older data can have only one on (APBOT,
// Oct 2026), and staff invoices then still fail, so the card shows OFF and one
// switch-on saves both. The first id is the record whose email template the
// card shows.
const companyChequeManualPaymentMethodIds: string[] = [
  ChamberMemberProManualPaymentMethodTypeEnum.InvoiceMe,
  ChamberMemberProManualPaymentMethodTypeEnum.Cheque,
];

// Saved identity for an INVOICE_ME record that the card must add. These are
// the backend defaults; the backend matches older invoices by this label.
const invoiceMeManualPaymentMethodIdentity = {
  label: "Invoice Me",
  description:
    "Send an invoice with the available chamber-provided payment instructions.",
};

const getSyncedManualPaymentMethodIds = (methodId: string): string[] =>
  companyChequeManualPaymentMethodIds.includes(methodId)
    ? companyChequeManualPaymentMethodIds
    : [methodId];

const manualPaymentMethodConfigs = [
  {
    lookupIds: companyChequeManualPaymentMethodIds,
    fallbackId: ChamberMemberProManualPaymentMethodTypeEnum.Cheque,
    label: "Company Cheque",
    description: "Enable to allow your chamber to accept company cheque payments.",
  },
  {
    lookupIds: [
      ChamberMemberProManualPaymentMethodTypeEnum.ETransfer,
      "e-transfer",
    ],
    fallbackId: ChamberMemberProManualPaymentMethodTypeEnum.ETransfer,
    label: "E-Transfer",
    description: "Enable to allow your chamber to accept E-transfer payments.",
  },
  {
    lookupIds: [ChamberMemberProManualPaymentMethodTypeEnum.Cash, "cash"],
    fallbackId: ChamberMemberProManualPaymentMethodTypeEnum.Cash,
    label: "Cash",
    description: "Enable to allow your chamber to accept cash payments.",
  },
  {
    lookupIds: [
      ChamberMemberProManualPaymentMethodTypeEnum.DirectDeposit,
      "direct-deposit",
    ],
    fallbackId: ChamberMemberProManualPaymentMethodTypeEnum.DirectDeposit,
    label: "Direct Deposit",
    description: "Enable to allow your chamber to accept direct deposit for payments.",
  },
];

// Design D:5719-5743 (S2/D-CS3): no toggle. The mapper saves `enabled` from
// whether an address is entered.
const chamberInvoiceCopyRows = [
  {
    key: "paidInvoiceReceiptCopy",
    title: "Add Chamber Email to Get the Copy of the Paid Invoice Receipts",
    helper: "A copy of every paid invoice receipt is sent to your chamber's email address.",
    label: "Enter the email address to receive the receipts.",
    emailLabel: "Paid invoice receipt copy email",
    requiredMessage: "Enter the email address to receive paid invoice receipts.",
  },
  {
    key: "createdInvoiceCopy",
    title: "Add Chamber Email to Get the Copy of the Created Invoices",
    helper: "A copy of every created invoice is sent to your chamber's email address.",
    label: "Enter the email address to receive the created invoices.",
    emailLabel: "Created invoice copy email",
    requiredMessage: "Enter the email address to receive created invoices.",
  },
] as const;

const INVOICE_EMAIL_FORMAT_MESSAGE =
  "Enter a full email address, for example accounts@yourchamber.ca.";

const isValidEmailAddress = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const designSubsectionTitleClassName =
  "text-base font-semibold text-[#0A0A0A]";
const designSubsectionHelperClassName = "mt-1 text-sm text-[#0A0A0A]";

const creditCardPaymentMethod =
  paymentMethods.find((method) => method.id === "credit-card") ||
  paymentMethods[0];

const GlobalSettingsTab = ({
  settings,
  chamberContactEmail = "",
  chamberWebsiteUrl,
  onOpenAutomatedEmails,
  isSandboxChamber,
  onChange,
  sectionControls,
  onStartStripeConnect,
  onRefreshStripeConnect,
  onDeleteStripeConnect,
  stripeConnectActionLoading,
  stripeConnectRefreshLoading,
  stripeConnectDeleteLoading,
  stripeConnectError,
  onStartSenderDomainVerification,
  onRefreshSenderDomainStatus,
  onRemoveSenderDomain,
  onSendSenderDomainTestEmail,
  senderDomainActionLoading,
  senderDomainRefreshLoading,
  senderDomainRemoveLoading,
  senderDomainTestEmailLoading,
  senderDomainError,
  hasUnsavedSettingsChanges,
}: GlobalSettingsTabProps) => {
  const noun = useCmpOrganizationNoun();
  const [templateMethodId, setTemplateMethodId] = useState<string | null>(null);
  // B13 behavioral fix: the Chamber Group Insurance modal edits a local
  // SNAPSHOT of the billing details (taken on open) and only commits to
  // settings on "Save Billing Details" — so Cancel actually discards. A
  // non-null draft doubles as the modal-open flag.
  const [chamberGroupInsuranceBillingDraft, setChamberGroupInsuranceBillingDraft] =
    useState<
      | ChamberSettingsFormState["paymentsAndTaxes"]["chamberGroupInsurancePayment"]["billingDetails"]
      | null
    >(null);
  const [chamberGroupInsuranceBillingErrors, setChamberGroupInsuranceBillingErrors] =
    useState<ChamberGroupInsuranceBillingErrors>({});
  const [additionalAccountsModalOpen, setAdditionalAccountsModalOpen] =
    useState(false);
  const [additionalAccountsRequest, setAdditionalAccountsRequest] =
    useState("");
  const { setup, membership, paymentsAndTaxes, email } = settings;
  // Design (11): Prorated stays off with a red hint while Fix Date is off.
  const [proratedBlocked, setProratedBlocked] = useState(false);
  // Red borders only after a blocked Save shows the required-field message.
  const missingRequired =
    sectionControls.chamberGlobals.errorText === CHAMBER_GLOBALS_REQUIRED_MESSAGE
      ? getMissingChamberGlobalsFields(setup)
      : [];
  const multipleBillingDates =
    setup.multipleBillingDates || defaultMultipleBillingDatesSettings();
  // Moeen (Oct 2 2026): Multi-Billing Dates show for Thunder Bay only
  // (backend allowlist flag).
  const multipleBillingDatesAvailable = !!setup.multipleBillingDatesAvailable;
  const multipleBillingDatesOn =
    multipleBillingDatesAvailable && multipleBillingDates.enabled;
  const multipleBillingDatesMissing =
    sectionControls.chamberGlobals.errorText === MULTIPLE_BILLING_DATES_REQUIRED_MESSAGE
      ? [
          !multipleBillingDates.firstBillingDate.month,
          !multipleBillingDates.secondBillingDate.month,
        ]
      : [false, false];
  const renewalFixedDateMissing =
    sectionControls.chamberGlobals.errorText === RENEWAL_FIXED_DATE_REQUIRED_MESSAGE &&
    setup.membershipRenewalFixedDateEnabled &&
    (!setup.membershipRenewalFixedDateMonth || !setup.membershipRenewalFixedDateDay);
  const invoicingOptionsDirty = sectionControls.invoicingOptions.isDirty;
  const eTransferEmailError = paymentsAndTaxes.eTransferEmail.trim() &&
    !isValidEmailAddress(paymentsAndTaxes.eTransferEmail.trim())
    ? "Enter a full email address in Set Email to Show on Invoice, for example payments@yourchamber.ca." : null;
  // The design marks both copy addresses as required. A blank saved address
  // is only flagged after staff edit this section, so a chamber that never
  // set them is not shown an error for other sections' work.
  const invoiceCopyErrors = chamberInvoiceCopyRows.map(({ key, requiredMessage }) => {
    const address = paymentsAndTaxes[key].email.trim();
    if (!address) {
      return invoicingOptionsDirty ? requiredMessage : null;
    }
    return isValidEmailAddress(address)
      ? null
      : INVOICE_EMAIL_FORMAT_MESSAGE;
  });
  const fiscalYearStartValue = getMonthDayValue(
    setup.fiscalYearStartMonth,
    setup.fiscalYearStartDay
  );
  const fiscalYearEndLabel = getFiscalYearEndLabel(fiscalYearStartValue);
  const stripeConnect = paymentsAndTaxes.stripeConnect;
  const effectiveStripeConnectMode = isSandboxChamber
    ? stripeConnect.mode
    : ChamberMemberProStripeConnectModeEnum.Live;
  const stripeIsTestMode =
    effectiveStripeConnectMode === ChamberMemberProStripeConnectModeEnum.Test;
  const stripeIsConnected =
    stripeConnect.status === ChamberMemberProStripeConnectStatusEnum.Connected &&
    stripeConnect.creditCardPaymentsEnabled;
  const creditCardPaymentsEnabled = stripeConnect.creditCardPaymentsEnabled;
  const eventTicketCreditCardPaymentsReady =
    getCmpCreditCardPaymentsReady(stripeConnect);
  const allEventTicketCreditCardPaymentsEnabled =
    paymentsAndTaxes.eventTicketCreditCardPaymentMode ===
    ChamberMemberProEventTicketCreditCardPaymentModeEnum.AllTickets;
  const nonMemberEventTicketCreditCardPaymentsEnabled =
    paymentsAndTaxes.eventTicketCreditCardPaymentMode !==
    ChamberMemberProEventTicketCreditCardPaymentModeEnum.Disabled;
  const stripeHasConnectedAccount = Boolean(stripeConnect.connectedAccountId);
  const stripeCanDelete =
    isSandboxChamber &&
    stripeIsTestMode &&
    (stripeHasConnectedAccount ||
      stripeConnect.status !==
        ChamberMemberProStripeConnectStatusEnum.NotConnected);
  const stripeCanStartOrContinue =
    !stripeHasConnectedAccount &&
    stripeConnect.status !== ChamberMemberProStripeConnectStatusEnum.Connected;
  const creditCardReadOnlyEnabled =
    stripeHasConnectedAccount && stripeIsConnected;

  const updateSettings = (updater: (current: ChamberSettingsFormState) => ChamberSettingsFormState) => {
    onChange(updater(settings));
  };

  const updateSetup = (
    updater: (
      current: ChamberSettingsFormState["setup"]
    ) => ChamberSettingsFormState["setup"]
  ) => {
    updateSettings((current) => ({
      ...current,
      setup: updater(current.setup),
    }));
  };

  const updateMembership = (
    updater: (
      current: ChamberSettingsFormState["membership"]
    ) => ChamberSettingsFormState["membership"]
  ) => {
    updateSettings((current) => ({
      ...current,
      membership: updater(current.membership),
    }));
  };

  const updatePaymentsAndTaxes = (
    updater: (
      current: ChamberSettingsFormState["paymentsAndTaxes"]
    ) => ChamberSettingsFormState["paymentsAndTaxes"]
  ) => {
    updateSettings((current) => ({
      ...current,
      paymentsAndTaxes: updater(current.paymentsAndTaxes),
    }));
  };

  // The read-only Invoice Footer Notes line keeps the design's 2 rows and
  // grows when the URL wraps further (3+ lines at 390px), so it is never cut.
  const invoiceFooterNotesText = getInvoiceFooterNotesText(chamberWebsiteUrl);
  const invoiceFooterNotesRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const textarea = invoiceFooterNotesRef.current;
    if (!textarea) {
      return undefined;
    }
    const fitToText = () => {
      textarea.style.height = "auto";
      const border = textarea.offsetHeight - textarea.clientHeight;
      textarea.style.height = `${textarea.scrollHeight + border}px`;
    };
    fitToText();
    window.addEventListener("resize", fitToText);
    return () => window.removeEventListener("resize", fitToText);
  }, [invoiceFooterNotesText]);

  // Edits in Invoicing Options also prefill blank invoice copy addresses with
  // the chamber contact email. The prefill is visible and is saved only with
  // this section, so other sections never switch the copies on silently.
  const updateInvoicingOptions = (
    updater: (
      current: ChamberSettingsFormState["paymentsAndTaxes"]
    ) => ChamberSettingsFormState["paymentsAndTaxes"]
  ) => {
    const fallbackEmail = chamberContactEmail.trim();

    updatePaymentsAndTaxes((current) => {
      const next = updater(current);
      if (!fallbackEmail) {
        return next;
      }

      const prefillCopy = (
        copy: ChamberSettingsFormState["paymentsAndTaxes"]["paidInvoiceReceiptCopy"],
        previous: ChamberSettingsFormState["paymentsAndTaxes"]["paidInvoiceReceiptCopy"]
      ) =>
        // Never overwrite the field that this edit changed, so staff can
        // clear or retype an address.
        copy === previous && !copy.email.trim()
          ? { ...copy, email: fallbackEmail }
          : copy;

      return {
        ...next,
        paidInvoiceReceiptCopy: prefillCopy(
          next.paidInvoiceReceiptCopy,
          current.paidInvoiceReceiptCopy
        ),
        createdInvoiceCopy: prefillCopy(
          next.createdInvoiceCopy,
          current.createdInvoiceCopy
        ),
      };
    });
  };

  const updateMembershipApplicationCommunicationOption = ({
    enabled,
    key,
  }: {
    enabled: boolean;
    key: MembershipApplicationCommunicationOptionKey;
  }) => {
    updateSetup((current) => ({
      ...current,
      membershipApplicationCommunicationOptions: {
        ...current.membershipApplicationCommunicationOptions,
        [key]: enabled,
      },
    }));
  };

  const handleFiscalYearStartDateChange = ({
    month,
    day,
  }: MonthDayCalendarValue) => {
    updateSetup((current) => ({
      ...current,
      fiscalYearStartMonth: month,
      fiscalYearStartDay: day,
    }));
  };

  const handleRenewalFixedDateChange = ({
    month,
    day,
  }: MonthDayCalendarValue) => {
    updateSetup((current) => ({
      ...current,
      membershipRenewalFixedDateMonth: month,
      membershipRenewalFixedDateDay: day,
    }));
  };

  const getManualPaymentMethod = (
    config: typeof manualPaymentMethodConfigs[number]
  ): PaymentMethod => {
    const savedMethods = config.lookupIds
      .map((lookupId) =>
        paymentsAndTaxes.manualPaymentMethods.find(
          (method) => method.id === lookupId
        )
      )
      .filter((method): method is PaymentMethod => Boolean(method));
    const existingMethod = savedMethods[0];
    const fallbackMethod =
      paymentMethods.find((method) => method.id === config.fallbackId) ||
      paymentMethods[1];

    return {
      ...fallbackMethod,
      ...existingMethod,
      id: existingMethod?.id || fallbackMethod.id,
      label: config.label,
      description: config.description,
      enabled: existingMethod
        ? savedMethods.every((method) => method.enabled)
        : fallbackMethod.enabled,
    };
  };

  const displayedManualPaymentMethods =
    manualPaymentMethodConfigs.map(getManualPaymentMethod);
  const selectedTemplateMethod = displayedManualPaymentMethods.find(
    (method) => method.id === templateMethodId
  );

  const upsertManualPaymentMethod = (
    method: PaymentMethod,
    updater: (method: PaymentMethod) => PaymentMethod
  ) => {
    updatePaymentsAndTaxes((current) => ({
      ...current,
      manualPaymentMethods: getSyncedManualPaymentMethodIds(method.id).reduce(
        (manualPaymentMethods, methodId) => {
          const existingMethod = manualPaymentMethods.find(
            (currentMethod) => currentMethod.id === methodId
          );
          // A missing paired record starts from the card's own values, with
          // its own saved identity.
          const newMethod: PaymentMethod =
            methodId === method.id
              ? method
              : {
                  ...method,
                  id: methodId,
                  ...(methodId ===
                  ChamberMemberProManualPaymentMethodTypeEnum.InvoiceMe
                    ? invoiceMeManualPaymentMethodIdentity
                    : {}),
                };
          const nextMethod = updater(existingMethod || newMethod);

          return existingMethod
            ? manualPaymentMethods.map((currentMethod) =>
                currentMethod.id === methodId ? nextMethod : currentMethod
              )
            : [...manualPaymentMethods, nextMethod];
        },
        current.manualPaymentMethods
      ),
    }));
  };

  // The card's template is written in full to every paired record, so
  // CHEQUE and INVOICE_ME send the same payment instructions.
  const updatePaymentTemplate = (
    method: PaymentMethod,
    field: "subject" | "body",
    value: string
  ) => {
    upsertManualPaymentMethod(method, (currentMethod) => ({
      ...currentMethod,
      emailSubject: field === "subject" ? value : method.emailSubject,
      emailBody: field === "body" ? value : method.emailBody,
    }));
  };

  const invoiceTaxCategoryKeys = Object.keys(
    paymentsAndTaxes.invoiceTaxSettings
  ) as Array<
    keyof ChamberSettingsFormState["paymentsAndTaxes"]["invoiceTaxSettings"]
  >;
  const primaryInvoiceTaxSettings =
    paymentsAndTaxes.invoiceTaxSettings.membershipInvoices;
  const taxSettingsEnabled = invoiceTaxCategoryKeys.some(
    (key) => paymentsAndTaxes.invoiceTaxSettings[key].enabled
  );
  const updateAllInvoiceTaxCategories = (
    updater: (
      category: ChamberSettingsFormState["paymentsAndTaxes"]["invoiceTaxSettings"]["membershipInvoices"]
    ) => ChamberSettingsFormState["paymentsAndTaxes"]["invoiceTaxSettings"]["membershipInvoices"]
  ) => {
    updatePaymentsAndTaxes((current) => ({
      ...current,
      invoiceTaxSettings: invoiceTaxCategoryKeys.reduce(
        (nextSettings, key) => ({
          ...nextSettings,
          [key]: updater(current.invoiceTaxSettings[key]),
        }),
        current.invoiceTaxSettings
      ),
    }));
  };

  const renderSetupSwitch = ({
    checked,
    onChange,
    ariaLabel,
    disabled = false,
    size = "medium",
  }: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    ariaLabel: string;
    disabled?: boolean;
    size?: "small" | "medium";
  }) => (
    <div className="flex shrink-0 items-center gap-3">
      <Switch
        checked={checked}
        size={size}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        sx={switchSx}
        inputProps={{ "aria-label": ariaLabel }}
      />
      <span
        className={`text-xs font-semibold uppercase ${
          checked ? "text-emerald-600" : "text-gray-500"
        }`}
      >
        {checked ? "ENABLED" : "DISABLED"}
      </span>
    </div>
  );

  const updateAccountAllocation = (
    type: ChamberSettingsFormState["paymentsAndTaxes"]["accountAllocations"][number]["type"],
    field: "accountNumber" | "accountName",
    value: string
  ) => {
    updatePaymentsAndTaxes((current) => ({
      ...current,
      accountAllocations: current.accountAllocations.map((accountAllocation) =>
        accountAllocation.type === type
          ? {
              ...accountAllocation,
              [field]: value,
            }
          : accountAllocation
      ),
    }));
  };

  const updateChamberGroupInsuranceBillingDraft = (
    field: keyof ChamberSettingsFormState["paymentsAndTaxes"]["chamberGroupInsurancePayment"]["billingDetails"],
    value: string
  ) => {
    setChamberGroupInsuranceBillingErrors((currentErrors) => {
      if (!currentErrors[field]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[field];
      return nextErrors;
    });
    setChamberGroupInsuranceBillingDraft((currentDraft) =>
      currentDraft
        ? {
            ...currentDraft,
            [field]: value,
          }
        : currentDraft
    );
  };

  const saveChamberGroupInsuranceBillingDraft = () => {
    if (chamberGroupInsuranceBillingDraft) {
      const nextErrors = getChamberGroupInsuranceBillingErrors({
        billingDetails: chamberGroupInsuranceBillingDraft,
      });

      if (Object.keys(nextErrors).length > 0) {
        setChamberGroupInsuranceBillingErrors(nextErrors);
        return;
      }

      const draftToCommit = chamberGroupInsuranceBillingDraft;
      updatePaymentsAndTaxes((current) => ({
        ...current,
        chamberGroupInsurancePayment: {
          ...current.chamberGroupInsurancePayment,
          billingDetails: draftToCommit,
        },
      }));
    }
    setChamberGroupInsuranceBillingErrors({});
    setChamberGroupInsuranceBillingDraft(null);
  };

  const closeChamberGroupInsuranceBillingDraft = () => {
    setChamberGroupInsuranceBillingErrors({});
    setChamberGroupInsuranceBillingDraft(null);
  };

  const handleSavePaymentOptions = () => {
    const insurancePayment = paymentsAndTaxes.chamberGroupInsurancePayment;
    if (insurancePayment.enabled) {
      const errors = getChamberGroupInsuranceBillingErrors({
        billingDetails: insurancePayment.billingDetails,
      });
      if (Object.keys(errors).length > 0) {
        setChamberGroupInsuranceBillingDraft({
          ...insurancePayment.billingDetails,
        });
        setChamberGroupInsuranceBillingErrors(errors);
        return;
      }
    }

    return sectionControls.paymentOptions.onSave();
  };

  const openAdditionalAccountsRequestEmail = () => {
    const bodyText = additionalAccountsRequest.trim()
      ? `Additional account allocation request:\n\n${additionalAccountsRequest.trim()}`
      : "Additional account allocation request:";

    window.location.href = `mailto:${STAFF_SUPPORT_EMAIL}?subject=${encodeURIComponent(
      "Additional account allocation request"
    )}&body=${encodeURIComponent(bodyText)}`;
    setAdditionalAccountsModalOpen(false);
    setAdditionalAccountsRequest("");
  };

  const openAdditionalAccountsModal = () => {
    setAdditionalAccountsModalOpen(true);
  };

  const handleCreditCardToggle = (checked: boolean) => {
    if (creditCardReadOnlyEnabled) {
      return;
    }

    if (checked && !stripeIsConnected) {
      // The card's design button opens the email template, so the toggle
      // owns both Stripe actions: start onboarding, or re-check an account
      // that Stripe has not finished connecting.
      if (stripeCanStartOrContinue) {
        onStartStripeConnect();
      } else {
        onRefreshStripeConnect();
      }
      return;
    }

    if (!checked && stripeCanDelete) {
      onDeleteStripeConnect();
    }
  };

  const renderPaymentMethodCard = ({
    title,
    description,
    enabled,
    onToggle,
    buttonLabel,
    onButtonClick,
    buttonDisabled,
    readOnlyEnabled = false,
    toggleDisabled = false,
  }: {
    title: string;
    description: string;
    enabled: boolean;
    onToggle: (enabled: boolean) => void;
    buttonLabel: string;
    onButtonClick: () => void;
    buttonDisabled?: boolean;
    readOnlyEnabled?: boolean;
    toggleDisabled?: boolean;
  }) => (
    <div className="flex min-h-[180px] flex-col bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-bold leading-tight text-black">
            {title}
          </h3>
          <p className="mt-4 text-sm leading-5 text-gray-900">
            {description}
          </p>
        </div>
        {readOnlyEnabled ? (
          <span className="inline-flex h-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 px-4 text-xs font-bold uppercase text-emerald-700">
            Enabled
          </span>
        ) : (
          renderSetupSwitch({
            checked: enabled,
            size: "small",
            onChange: onToggle,
            ariaLabel: `${title} enabled`,
            disabled: toggleDisabled,
          })
        )}
      </div>
      <button
        type="button"
        onClick={onButtonClick}
        disabled={buttonDisabled}
        className={`mt-auto inline-flex h-9 w-fit items-center justify-center rounded-md px-4 text-xs font-bold text-white transition disabled:cursor-not-allowed ${
          buttonDisabled
            ? "bg-gray-500"
            : "bg-cpaGold hover:bg-amber-500"
        }`}
      >
        {buttonLabel}
      </button>
    </div>
  );

  const renderManualPaymentMethod = (method: PaymentMethod) => (
    <React.Fragment key={method.id}>
      {renderPaymentMethodCard({
        title: method.label,
        description: applyCmpOrganizationNoun(method.description, noun),
        enabled: method.enabled,
        onToggle: (enabled) =>
          upsertManualPaymentMethod(method, (currentMethod) => ({
            ...currentMethod,
            enabled,
          })),
        buttonLabel: "View and Edit Automated Email Template",
        onButtonClick: () => setTemplateMethodId(method.id),
        buttonDisabled: !method.enabled,
      })}
    </React.Fragment>
  );

  const billingDetails =
    paymentsAndTaxes.chamberGroupInsurancePayment.billingDetails;

  return (
    <div className="space-y-6">
      <section className={settingsSectionCardClassName}>
        <div>
          <h2 className={settingsSectionTitleClassName}>
            {noun.Singular} Global Settings
          </h2>
          <p className={settingsSectionDescriptionClassName}>
            Setup your {noun.possessive} global settings.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-8 xl:grid-cols-3">
          <ChamberGlobalField
            title="Fiscal Year"
            helper="Set up your membership Fiscal Year"
            headerClassName="xl:min-h-[80px]"
            required
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <CalendarDateInput
                mode="monthDay"
                ariaLabel="Fiscal year start date"
                value={{
                  month: setup.fiscalYearStartMonth,
                  day: setup.fiscalYearStartDay,
                }}
                onChange={handleFiscalYearStartDateChange}
                inputClassName={`${chamberGlobalInputClassName} w-full${
                  missingRequired.includes("fiscalYear") ? " !border-[#F21B1B]" : ""
                }`}
              />
              <GlobalReadOnlyDateInput
                ariaLabel="Fiscal year end date"
                value={fiscalYearEndLabel}
              />
            </div>
          </ChamberGlobalField>

          <ChamberGlobalField
            title="Currency"
            helper="Select the currency used for all transactions."
            headerClassName="xl:min-h-[80px]"
            required
          >
            <GlobalSelectInput
              ariaLabel="Currency"
              error={missingRequired.includes("currency")}
              value={setup.currency}
              onChange={(currency) =>
                updateSetup((current) => ({ ...current, currency }))
              }
              options={[
                {
                  value: "CAD - Canadian Dollar (CAD)",
                  label: "CAD - Canadian Dollar (CAD)",
                },
                {
                  value: "USD - United States Dollar (USD)",
                  label: "USD - United States Dollar (USD)",
                },
              ]}
              icon="chevron"
            />
          </ChamberGlobalField>

          <ChamberGlobalField
            title="Time Zone"
            helper="Select the time zone for all scheduled emails and automation."
            headerClassName="xl:min-h-[80px]"
            required
          >
            <GlobalSelectInput
              ariaLabel="Time zone"
              error={missingRequired.includes("timeZone")}
              value={setup.timeZone}
              onChange={(timeZone) =>
                updateSetup((current) => ({ ...current, timeZone }))
              }
              options={northAmericanTimeZones.map((timeZone) => ({
                value: timeZone,
                label: timeZoneDisplayLabelByValue[timeZone] || timeZone,
              }))}
              icon="chevron"
            />
          </ChamberGlobalField>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-x-8 gap-y-8 xl:grid-cols-3">
          <ChamberGlobalField
            title="Membership Renewal Fix Date"
            helper={`If your ${noun.singular} offers membership renewals on the same date each of your Fiscal Year, please enable and enter one date.`}
            headerClassName="xl:min-h-[152px]"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <EnabledSwitchControl
                checked={setup.membershipRenewalFixedDateEnabled}
                onChange={(membershipRenewalFixedDateEnabled) => {
                  setProratedBlocked(false);
                  // Design (11): prorated fees need a renewal billing date, so
                  // turning the fix date off (with no Multi-Billing Dates)
                  // also turns prorated fees off; Fix Date and Multi-Billing
                  // Dates turn each other off (TB11 13741-13746). One update:
                  // the section helpers each start from the same props value.
                  const multipleBillingDatesStayOn =
                    !membershipRenewalFixedDateEnabled &&
                    !!multipleBillingDatesOn;
                  updateSettings((current) => ({
                    ...current,
                    setup: {
                      ...current.setup,
                      membershipRenewalFixedDateEnabled,
                      ...(membershipRenewalFixedDateEnabled &&
                      current.setup.multipleBillingDates?.enabled
                        ? {
                            multipleBillingDates: {
                              ...current.setup.multipleBillingDates,
                              enabled: false,
                            },
                          }
                        : {}),
                    },
                    membership: membershipRenewalFixedDateEnabled ||
                      multipleBillingDatesStayOn
                      ? current.membership
                      : {
                          ...current.membership,
                          globalToggleState: {
                            ...current.membership.globalToggleState,
                            prorate: false,
                          },
                        },
                  }));
                }}
                ariaLabel="Enable membership renewal fixed date"
              />
              <div className="w-full sm:flex-1">
                {/* Claude Design (13) CMP Dashboard 6171-6189: empty with
                    "MMM dd" while off or not chosen; required while on. */}
                <CalendarDateInput
                  mode="monthDay"
                  ariaLabel="Membership renewal fixed date"
                  disabled={!setup.membershipRenewalFixedDateEnabled}
                  placeholder="MMM dd"
                  value={
                    setup.membershipRenewalFixedDateEnabled
                      ? {
                          month: setup.membershipRenewalFixedDateMonth,
                          day: setup.membershipRenewalFixedDateDay,
                        }
                      : { month: "", day: "" }
                  }
                  onChange={handleRenewalFixedDateChange}
                  inputClassName={`${chamberGlobalInputClassName} w-full${
                    renewalFixedDateMissing ? " !border-[#F21B1B]" : ""
                  }`}
                />
                {renewalFixedDateMissing ? (
                  <p className="mt-[6px] text-[13px] leading-[normal] text-[#F21B1B]" role="alert">
                    {RENEWAL_FIXED_DATE_REQUIRED_MESSAGE}
                  </p>
                ) : null}
              </div>
            </div>
          </ChamberGlobalField>

          <ChamberGlobalField
            title="Prorated Company Membership Fees"
            helper={`If your ${noun.singular} has enabled Membership Renewal Fix Date you would like to set up a prorated membership fee for new members only please enable.`}
            headerClassName="xl:min-h-[152px]"
          >
            <EnabledSwitchControl
              checked={membership.globalToggleState.prorate}
              onChange={(prorate) => {
                if (
                  prorate &&
                  !setup.membershipRenewalFixedDateEnabled &&
                  !multipleBillingDatesOn
                ) {
                  setProratedBlocked(true);
                  return;
                }
                setProratedBlocked(false);
                updateMembership((current) => ({
                  ...current,
                  globalToggleState: {
                    ...current.globalToggleState,
                    prorate,
                  },
                }));
              }}
              ariaLabel="Prorated company membership fees"
            />
            {proratedBlocked ? (
              <p className="mt-2 text-[13px] leading-[normal] text-[#F21B1B]" role="alert">
                {multipleBillingDatesAvailable
                  ? "Enable Membership Renewal Fix Date or Membership Renewal Multi-Billing Dates first."
                  : "Enable Membership Renewal Fix Date first to use prorated membership fees."}
              </p>
            ) : null}
          </ChamberGlobalField>

          <ChamberGlobalField
            title="Provincial Chamber Of Commerce Fee"
            helper={`Enable this option if your ${noun.singular} collects a provincial Chamber of Commerce membership fee from your members. Enter the fee amount in the field below. If you want this fee to appear as a separate line on member invoices, select the checkbox.`}
            headerClassName="xl:min-h-[152px]"
          >
            <div className="space-y-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <EnabledSwitchControl
                  checked={paymentsAndTaxes.provincialChamberFee.enabled}
                  onChange={(enabled) =>
                    updatePaymentsAndTaxes((current) => ({
                      ...current,
                      provincialChamberFee: {
                        ...current.provincialChamberFee,
                        enabled,
                      },
                    }))
                  }
                  ariaLabel="Provincial chamber fee"
                />
                <div className="w-full sm:flex-1">
                  <MoneyInput
                    value={paymentsAndTaxes.provincialChamberFee.amount}
                    disabled={!paymentsAndTaxes.provincialChamberFee.enabled}
                    onChange={(amount) =>
                      updatePaymentsAndTaxes((current) => ({
                        ...current,
                        provincialChamberFee: {
                          ...current.provincialChamberFee,
                          amount,
                        },
                      }))
                    }
                    placeholder="$00.00"
                    ariaLabel="Provincial chamber fee amount"
                    className={chamberGlobalInputClassName}
                  />
                </div>
              </div>
              <label className="flex items-center gap-3 text-sm font-medium text-gray-800">
                <Checkbox
                  checked={
                    paymentsAndTaxes.provincialChamberFee.showSeparateOnInvoice
                  }
                  disabled={!paymentsAndTaxes.provincialChamberFee.enabled}
                  onChange={(event) =>
                    updatePaymentsAndTaxes((current) => ({
                      ...current,
                      provincialChamberFee: {
                        ...current.provincialChamberFee,
                        showSeparateOnInvoice: event.target.checked,
                      },
                    }))
                  }
                  sx={inlineChoiceControlSx}
                />
                Show separate on invoice
              </label>
            </div>
          </ChamberGlobalField>

          {multipleBillingDatesAvailable ? (
            <ChamberGlobalField
              title="Membership Renewal Multi-Billing Dates"
              helper={`Enable this feature if your ${noun.singular} offers prorated rates with multiple renewal billing dates for new memberships. Set the default renewal amount and terms when configuring prorated fees for each membership type.`}
              headerClassName="xl:min-h-[152px]"
            >
              <div className="flex flex-col gap-4">
                <EnabledSwitchControl
                  checked={multipleBillingDates.enabled}
                  onChange={(enabled) => {
                    setProratedBlocked(false);
                    // TB11 13741-13746: Multi-Billing Dates and Fix Date turn
                    // each other off; with neither on, prorated fees turn off.
                    // Design: no default dates, so a first enable starts empty.
                    updateSettings((current) => {
                      const currentDates =
                        current.setup.multipleBillingDates ||
                        defaultMultipleBillingDatesSettings();
                      const nextDates = enabled
                        ? {
                            enabled: true,
                            firstBillingDate: { month: "", day: "" },
                            secondBillingDate: { month: "", day: "" },
                          }
                        : { ...currentDates, enabled: false };
                      const fixDateOn =
                        !enabled && current.setup.membershipRenewalFixedDateEnabled;
                      return {
                        ...current,
                        setup: {
                          ...current.setup,
                          membershipRenewalFixedDateEnabled: enabled
                            ? false
                            : current.setup.membershipRenewalFixedDateEnabled,
                          multipleBillingDates: nextDates,
                        },
                        membership:
                          enabled || fixDateOn
                            ? current.membership
                            : {
                                ...current.membership,
                                globalToggleState: {
                                  ...current.membership.globalToggleState,
                                  prorate: false,
                                },
                              },
                      };
                    });
                  }}
                  ariaLabel="Membership renewal multi-billing dates"
                />
                {multipleBillingDates.enabled ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {(
                      [
                        ["firstBillingDate", "First Billing Date", "Select First Billing Date"],
                        ["secondBillingDate", "Second Billing Date", "Select Second Billing Date"],
                      ] as const
                    ).map(([key, label, placeholder], index) => (
                      <div key={key} className="flex min-w-0 flex-col gap-[6px]">
                        <span className="text-[13px] font-semibold text-[#0A0A0A]">
                          {label}
                        </span>
                        <CalendarDateInput
                          mode="monthDay"
                          ariaLabel={label}
                          placeholder={placeholder}
                          value={multipleBillingDates[key]}
                          onChange={(date) =>
                            updateSetup((current) => ({
                              ...current,
                              multipleBillingDates: {
                                ...(current.multipleBillingDates ||
                                  defaultMultipleBillingDatesSettings()),
                                [key]: date,
                              },
                            }))
                          }
                          inputClassName={`${chamberGlobalInputClassName} w-full${
                            multipleBillingDatesMissing[index]
                              ? " !border-[#F21B1B]"
                              : ""
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </ChamberGlobalField>
          ) : null}
        </div>

        <SectionSaveReset {...sectionControls.chamberGlobals} />
      </section>

      <ScreenshotSection
        title="Tax Settings"
        description="Enable and set the tax rate to be charged on all invoices."
      >
        <div className={screenshotSectionClassName}>
          <div className="px-2 sm:px-8">
            <h3 className="text-base font-bold text-black">
              Tax Registration Number
            </h3>
            <p className="mt-5 text-sm leading-6 text-black">
              If the {noun.singular} is registered to collect GST/HST and is charging tax, the GST/HST registration number should appear on invoices/receipts when required. Enter number below.
            </p>
            <input
              value={paymentsAndTaxes.taxRegistrationNumber}
              onChange={(event) =>
                updatePaymentsAndTaxes((current) => ({
                  ...current,
                  taxRegistrationNumber: event.target.value,
                }))
              }
              placeholder={`Enter Your ${noun.Singular} Tax Registration Number`}
              className={`${setupSectionInputClassName} mt-4 max-w-[480px]`}
            />

            <h3 className="mt-6 text-base font-bold text-black">
              Tax Settings
            </h3>
            <p className="mt-5 max-w-6xl text-sm leading-6 text-black">
              If your {noun.singular} <b>DOES NOT</b> charge tax on invoices
              please make sure setting is <b>DISABLED.</b> If enabled enter the
              applicable tax code, which will show on invoices and the
              percentage tax rate. If your {noun.singular} charges more than
              one tax rate, enter each rate in the appropriate field. Taxes will
              show on all invoices.
            </p>
            <div className="mt-8">
              {renderSetupSwitch({
                checked: taxSettingsEnabled,
                onChange: (enabled) =>
                  updateAllInvoiceTaxCategories((category) => ({
                    ...category,
                    enabled,
                  })),
                ariaLabel: "Tax settings enabled",
              })}
              <div className="mt-7 grid max-w-3xl grid-cols-1 gap-x-14 gap-y-4 sm:grid-cols-[1fr_280px_auto]">
                <input
                  value={primaryInvoiceTaxSettings.firstTaxRateLabel}
                  disabled={!taxSettingsEnabled}
                  onChange={(event) =>
                    updateAllInvoiceTaxCategories((category) => ({
                      ...category,
                      firstTaxRateLabel: event.target.value,
                    }))
                  }
                  placeholder="Enter Tax Code"
                  className={setupSectionInputClassName}
                />
                <DecimalNumberInput
                  value={primaryInvoiceTaxSettings.firstTaxRatePercentage}
                  disabled={!taxSettingsEnabled}
                  onChange={(firstTaxRatePercentage) =>
                    updateAllInvoiceTaxCategories((category) => ({
                      ...category,
                      firstTaxRatePercentage,
                    }))
                  }
                  placeholder="Enter Tax Rate"
                  ariaLabel="First tax rate percentage"
                  className={setupSectionInputClassName}
                />
                <span className="self-center text-base font-semibold text-black">
                  %
                </span>
                <input
                  value={primaryInvoiceTaxSettings.secondTaxRateLabel}
                  disabled={!taxSettingsEnabled}
                  onChange={(event) =>
                    updateAllInvoiceTaxCategories((category) => ({
                      ...category,
                      secondTaxRateLabel: event.target.value,
                    }))
                  }
                  placeholder="Enter Tax Code"
                  className={setupSectionInputClassName}
                />
                <DecimalNumberInput
                  value={primaryInvoiceTaxSettings.secondTaxRatePercentage}
                  disabled={!taxSettingsEnabled}
                  onChange={(secondTaxRatePercentage) =>
                    updateAllInvoiceTaxCategories((category) => ({
                      ...category,
                      secondTaxRatePercentage,
                    }))
                  }
                  placeholder="Enter Tax Rate"
                  ariaLabel="Second tax rate percentage"
                  className={setupSectionInputClassName}
                />
                <span className="self-center text-base font-semibold text-black">
                  %
                </span>
              </div>
            </div>
          </div>
        </div>
        <SectionSaveReset {...sectionControls.taxSettings} />
      </ScreenshotSection>

      <ScreenshotSection
        title="Account Allocation"
        description="Assign an accounting account number to track the various sales options below. Accounts will not be able to be edited."
      >
        <div className={screenshotSectionClassName}>
          <div className="overflow-x-auto px-0 sm:px-8">
            <div className="min-w-[820px]">
              <div className="grid grid-cols-[260px_220px_1fr] gap-8 bg-[#F7F7F8] px-7 py-3 text-xs font-semibold uppercase tracking-[0.04em] text-[#0A0A0A]">
                <div>Account Allocation</div>
                <div>Account Number</div>
                <div>Account Name</div>
              </div>
              {paymentsAndTaxes.accountAllocations.map((accountAllocation) => (
                <div
                  key={accountAllocation.type}
                  className="grid grid-cols-[260px_220px_1fr] items-center gap-8 px-7 py-10"
                >
                  <div className="text-base font-bold text-black">
                    {accountAllocation.label}
                  </div>
                  <input
                    value={accountAllocation.accountNumber}
                    onChange={(event) =>
                      updateAccountAllocation(
                        accountAllocation.type,
                        "accountNumber",
                        event.target.value
                      )
                    }
                    placeholder="Enter Account #"
                    className={setupSectionInputClassName}
                  />
                  <input
                    value={accountAllocation.accountName}
                    onChange={(event) =>
                      updateAccountAllocation(
                        accountAllocation.type,
                        "accountName",
                        event.target.value
                      )
                    }
                    placeholder="Enter Account Name"
                    className={setupSectionInputClassName}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <button
          type="button"
          onMouseDown={openAdditionalAccountsModal}
          onClick={openAdditionalAccountsModal}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              openAdditionalAccountsModal();
            }
          }}
          className="mt-7 flex items-center gap-2 text-left text-sm font-semibold text-[#E1A731] transition hover:text-[#C6912A]"
        >
          <span>+</span>
          If your {noun.singular} would want to add additional accounts please click here to contact support
        </button>
        <SectionSaveReset {...sectionControls.accountAllocation} />
      </ScreenshotSection>

      <ScreenshotSection
        title="Payment Options"
        description={`Select which payment methods your ${noun.singular} will accept.`}
      >
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
          {renderPaymentMethodCard({
            title: creditCardPaymentMethod.label,
            description: `Enable to allow your ${noun.singular} to accept credit card payments.`,
            enabled: creditCardPaymentsEnabled,
            onToggle: handleCreditCardToggle,
            buttonLabel: "View and Edit Automated Email Template",
            // Design D:5557: card payments have no manual template, so the
            // button opens the Automated Emails tab.
            onButtonClick: () => onOpenAutomatedEmails?.(),
            buttonDisabled: !creditCardPaymentsEnabled || !onOpenAutomatedEmails,
            readOnlyEnabled: creditCardReadOnlyEnabled,
            toggleDisabled:
              stripeConnectActionLoading ||
              stripeConnectRefreshLoading ||
              stripeConnectDeleteLoading,
          })}
          {displayedManualPaymentMethods.map(renderManualPaymentMethod)}
          {renderPaymentMethodCard({
            title: "Chamber Group Insurance",
            description:
              "Enable to allow Chamber Group Insurance to pay member, membership fee.",
            enabled: paymentsAndTaxes.chamberGroupInsurancePayment.enabled,
            onToggle: (enabled) =>
              updatePaymentsAndTaxes((current) => ({
                ...current,
                chamberGroupInsurancePayment: {
                  ...current.chamberGroupInsurancePayment,
                  enabled,
                },
              })),
            buttonLabel: "Enter billing details",
            onButtonClick: () => {
              setChamberGroupInsuranceBillingErrors({});
              setChamberGroupInsuranceBillingDraft({ ...billingDetails });
            },
            // Matches the manual-method cards' disabled-button pattern: the
            // billing-details entry point is inert while the option is off.
            buttonDisabled:
              !paymentsAndTaxes.chamberGroupInsurancePayment.enabled,
          })}
        </div>

        {stripeConnectError && (
          <div className="mt-5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            {stripeConnectError}
          </div>
        )}
        <div className={`${screenshotSectionClassName} mt-5`}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_260px] lg:items-center">
            <div className="pl-0 sm:pl-3">
              <h3 className="text-base font-bold text-black">
                Credit Card Processing Fee Option
              </h3>
              <p className="mt-4 max-w-4xl text-sm leading-6 text-black">
                If your {noun.singular} would like to add a credit card processing fee that will be applied for members using credit card payment option, enable and set percentage. Users who select credit card payment options will be made aware of the fee prior to proceeding. This fee will be added to the total payment amount at the time of checkout.
              </p>
            </div>
            <div className="space-y-5">
              {renderSetupSwitch({
                checked: paymentsAndTaxes.cardProcessingFee.enabled,
                onChange: (enabled) =>
                  updatePaymentsAndTaxes((current) => ({
                    ...current,
                    cardProcessingFee: {
                      ...current.cardProcessingFee,
                      enabled,
                    },
                  })),
                ariaLabel: "Credit card processing fee",
              })}
              <div className="flex items-center gap-3">
                <DecimalNumberInput
                  value={paymentsAndTaxes.cardProcessingFee.percentage}
                  disabled={!paymentsAndTaxes.cardProcessingFee.enabled}
                  onChange={(percentage) =>
                    updatePaymentsAndTaxes((current) => ({
                      ...current,
                      cardProcessingFee: {
                        ...current.cardProcessingFee,
                        percentage,
                      },
                    }))
                  }
                  placeholder="0.0"
                  ariaLabel="Credit card processing fee percentage"
                  className={`${setupSectionInputClassName} max-w-[92px]`}
                />
                <span className="text-base font-semibold text-black">
                  %
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={`${screenshotSectionClassName} mt-5`}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_260px] lg:items-center">
            <div className="pl-0 sm:pl-3">
              <h3 className="text-base font-bold text-black">
                Monthly Membership Payment Administrative Fee Option
              </h3>
              <p className="mt-4 max-w-4xl text-sm leading-6 text-black">
                If your {noun.singular} would like to add an administrative transaction fee for processing monthly membership payments that will be applied for members using monthly card payment options, enable and set dollar amount per transaction. Users who select monthly payment options will be made aware of the Administrative Transaction fee prior to proceeding. This fee will be added to the total monthly payment amount.
                <br />
                Payment from Chamber Group Insurance will NOT be subject to any monthly Administrative Transaction Fees.
              </p>
            </div>
            <div className="space-y-5">
              {renderSetupSwitch({
                checked:
                  paymentsAndTaxes.monthlyMembershipAdministrativeFee.enabled,
                onChange: (enabled) =>
                  updatePaymentsAndTaxes((current) => ({
                    ...current,
                    monthlyMembershipAdministrativeFee: {
                      ...current.monthlyMembershipAdministrativeFee,
                      enabled,
                    },
                  })),
                ariaLabel: "Monthly membership administrative fee",
              })}
              <div className="flex items-center gap-3">
                <MoneyInput
                  value={paymentsAndTaxes.monthlyMembershipAdministrativeFee.amount}
                  disabled={
                    !paymentsAndTaxes.monthlyMembershipAdministrativeFee.enabled
                  }
                  onChange={(amount) =>
                    updatePaymentsAndTaxes((current) => ({
                      ...current,
                      monthlyMembershipAdministrativeFee: {
                        ...current.monthlyMembershipAdministrativeFee,
                        amount,
                      },
                    }))
                  }
                  placeholder="$0.00"
                  ariaLabel="Monthly membership administrative fee amount"
                  className={`${setupSectionInputClassName} max-w-[92px]`}
                />
                <span className="text-base font-semibold text-black">
                  /transaction
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={`${screenshotSectionClassName} mt-5`}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_260px] lg:items-center">
            <div className="pl-0 sm:pl-3">
              <h3 className="text-base font-bold text-black">
                Event Ticket Sales - Credit Card Payment option only on all
                event ticket sales
              </h3>
              <p className="mt-4 max-w-4xl text-sm leading-6 text-black">
                Enable this option to require all event ticket purchases to be
                paid by credit card.
              </p>
            </div>
            {renderSetupSwitch({
              checked: allEventTicketCreditCardPaymentsEnabled,
              disabled: !eventTicketCreditCardPaymentsReady,
              onChange: (enabled) =>
                updatePaymentsAndTaxes((current) => ({
                  ...current,
                  eventTicketCreditCardPaymentMode: enabled
                    ? ChamberMemberProEventTicketCreditCardPaymentModeEnum.AllTickets
                    : ChamberMemberProEventTicketCreditCardPaymentModeEnum.NonMemberTickets,
                })),
              ariaLabel: "Credit card payment required for all event ticket sales",
            })}
          </div>
        </div>

        <div className={`${screenshotSectionClassName} mt-5`}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_260px] lg:items-center">
            <div className="pl-0 sm:pl-3">
              <h3 className="text-base font-bold text-black">
                Event Ticket Sales - Credit Card Payment option for
                Non-Members ticket sales only
              </h3>
              <p className="mt-4 max-w-4xl text-sm leading-6 text-black">
                Enable this option to require non-members to pay for event
                tickets by credit card.
              </p>
            </div>
            {renderSetupSwitch({
              checked: nonMemberEventTicketCreditCardPaymentsEnabled,
              disabled: !eventTicketCreditCardPaymentsReady,
              onChange: (enabled) =>
                updatePaymentsAndTaxes((current) => ({
                  ...current,
                  eventTicketCreditCardPaymentMode: enabled
                    ? current.eventTicketCreditCardPaymentMode ===
                      ChamberMemberProEventTicketCreditCardPaymentModeEnum.AllTickets
                      ? ChamberMemberProEventTicketCreditCardPaymentModeEnum.AllTickets
                      : ChamberMemberProEventTicketCreditCardPaymentModeEnum.NonMemberTickets
                    : ChamberMemberProEventTicketCreditCardPaymentModeEnum.Disabled,
                })),
              ariaLabel:
                "Credit card payment required for non-member event ticket sales",
            })}
          </div>
        </div>

        {!eventTicketCreditCardPaymentsReady && (
          <p className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
            To use these event ticket options, turn on Credit Card in Payment
            Options and finish the Stripe setup first.
          </p>
        )}
        <SectionSaveReset
          {...sectionControls.paymentOptions}
          onSave={handleSavePaymentOptions}
        />
      </ScreenshotSection>

      <ScreenshotSection
        title="Invoicing Options"
        description={`Enter and select the invoicing options you want to enable for your ${noun.singular}.`}
      >
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-[6px]">
            <h3 className={designSubsectionTitleClassName}>
              Invoice Start Number
            </h3>
            <p className="text-sm text-[#0A0A0A]">
              Enter the number you want your invoices to start from.
            </p>
            {/* Production rule: the start number locks after the first invoice. */}
            <p className="text-sm leading-6 text-black">
              {paymentsAndTaxes.invoiceStartNumberLocked
                ? "Invoice numbering has started. The starting number is locked. Existing invoices will not change."
                : "You can change this value until the first invoice is created."}
            </p>
            <WholeNumberInput
              value={paymentsAndTaxes.invoiceStartNumber}
              onChange={(invoiceStartNumber) =>
                updateInvoicingOptions((current) => ({
                  ...current,
                  invoiceStartNumber,
                }))
              }
              onBlur={() =>
                updatePaymentsAndTaxes((current) => ({
                  ...current,
                  invoiceStartNumber: normalizeInvoiceStartNumber({
                    value: current.invoiceStartNumber,
                  }),
                }))
              }
              placeholder="Invoice Start Number"
              ariaLabel="Invoice start number"
              readOnly={paymentsAndTaxes.invoiceStartNumberLocked}
              className={`${globalSettingsDesignInputClassName} max-w-[320px] ${
                paymentsAndTaxes.invoiceStartNumberLocked
                  ? "cursor-not-allowed bg-gray-100 text-gray-500"
                  : ""
              }`}
            />
          </div>

          {chamberInvoiceCopyRows.map(({ key, title, helper, label, emailLabel }, index) => (
            <div key={key} className="flex flex-col gap-[10px]">
              <div>
                <h3 className={designSubsectionTitleClassName}>
                  {applyCmpOrganizationNoun(title, noun)}
                </h3>
                <p className={designSubsectionHelperClassName}>
                  {applyCmpOrganizationNoun(helper, noun)}
                </p>
              </div>
              <label className="flex max-w-[320px] flex-col gap-[6px]">
                <span className="text-[13px] font-semibold text-[#0A0A0A]">
                  {label} <span className="text-[#E24C4C]">*</span>
                </span>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  aria-label={emailLabel}
                  aria-required="true"
                  aria-invalid={Boolean(invoiceCopyErrors[index])}
                  aria-describedby={invoiceCopyErrors[index] ? `${key}-error` : undefined}
                  className={`${globalSettingsDesignInputClassName} ${invoiceCopyErrors[index] ? "border-red-500" : ""}`}
                  placeholder={`Enter ${noun.Singular} Email`}
                  value={paymentsAndTaxes[key].email}
                  onChange={(event) =>
                    updateInvoicingOptions((current) => ({
                      ...current,
                      [key]: { ...current[key], email: event.target.value },
                    }))
                  }
                />
              </label>
              {invoiceCopyErrors[index] && (
                <p id={`${key}-error`} role="alert" className="text-sm text-red-600">
                  {invoiceCopyErrors[index]}
                </p>
              )}
            </div>
          ))}

          <div className="flex flex-col gap-[10px]">
            <div className="flex items-start justify-between gap-5">
              <div>
                <h3 className={designSubsectionTitleClassName}>
                  Membership Discount Option For Internal Use Only
                </h3>
                <p className={designSubsectionHelperClassName}>
                  Enable this option if your {noun.singular} would like to apply internal discounts to regular or prorated memberships. Once enabled, a discount description field and discount amount field will appear (See Sample Below) in the Create Member-member Section. If both a description and amount are entered, the discount will appear as a separate line item on the invoice. If left blank, no discount will be shown.
                </p>
              </div>
              {renderSetupSwitch({
                checked: paymentsAndTaxes.membershipDiscount.enabled,
                onChange: (enabled) =>
                  updateInvoicingOptions((current) => ({
                    ...current,
                    membershipDiscount: {
                      ...current.membershipDiscount,
                      enabled,
                    },
                  })),
                ariaLabel: "Membership discount option",
              })}
            </div>
            {paymentsAndTaxes.membershipDiscount.enabled && (
              // Design D:5758-5765 sample: an illustration of the Create
              // Member fields, not settings data.
              <div
                aria-hidden="true"
                className="grid grid-cols-[minmax(0,1fr)_160px] gap-3"
              >
                <input
                  type="text"
                  disabled
                  tabIndex={-1}
                  placeholder="Enter Discount Description"
                  className={`${globalSettingsDesignInputClassName} bg-[#F7F7F8]`}
                />
                <input
                  type="text"
                  disabled
                  tabIndex={-1}
                  placeholder="Enter Amount"
                  className={globalSettingsDesignInputClassName}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-[10px]">
            <div className="flex items-start justify-between gap-5">
              <div>
                <h3 className={designSubsectionTitleClassName}>
                  One-Time Onboarding Fee For New Members
                </h3>
                <p className={designSubsectionHelperClassName}>
                  Enable this option if your {noun.singular} has a one-time onboarding fee for members. Enter amount below and it will show up on an invoice.
                </p>
              </div>
              {renderSetupSwitch({
                checked: paymentsAndTaxes.oneTimeOnboardingFee.enabled,
                onChange: (enabled) =>
                  updateInvoicingOptions((current) => ({
                    ...current,
                    oneTimeOnboardingFee: {
                      ...current.oneTimeOnboardingFee,
                      enabled,
                    },
                  })),
                ariaLabel: "One-time onboarding fee for new members",
              })}
            </div>
            {paymentsAndTaxes.oneTimeOnboardingFee.enabled && (
              <>
                {/* Design D:5782-5786 shows description and amount only. A
                    saved fee code is kept and still saved unchanged. */}
                <div className="grid grid-cols-[minmax(0,1fr)_160px] gap-3">
                  <input
                    value={paymentsAndTaxes.oneTimeOnboardingFee.description}
                    onChange={(event) =>
                      updateInvoicingOptions((current) => ({
                        ...current,
                        oneTimeOnboardingFee: {
                          ...current.oneTimeOnboardingFee,
                          description: event.target.value,
                        },
                      }))
                    }
                    placeholder="One-Time Onboarding Fee"
                    aria-label="One-time onboarding fee description"
                    className={`${globalSettingsDesignInputClassName} bg-[#F7F7F8]`}
                  />
                  <MoneyInput
                    value={paymentsAndTaxes.oneTimeOnboardingFee.amount}
                    onChange={(amount) =>
                      updateInvoicingOptions((current) => ({
                        ...current,
                        oneTimeOnboardingFee: {
                          ...current.oneTimeOnboardingFee,
                          amount,
                        },
                      }))
                    }
                    placeholder="Enter Amount"
                    ariaLabel="One-time onboarding fee amount"
                    className={globalSettingsDesignInputClassName}
                  />
                </div>
                <p className="text-sm leading-6 text-gray-700">
                  This fee is taxed normally, but membership discounts and proration
                  do not reduce it.
                </p>
              </>
            )}
          </div>

          <div className="flex flex-col gap-[14px]">
            <div>
              <h3 className={designSubsectionTitleClassName}>
                Invoice Payment Terms and Invoice Footer Note
              </h3>
              <p className={designSubsectionHelperClassName}>
                The information below will appear at bottom of all invoices.
                <br />
                Payment is due by (enter term). Overdue balances may be subject to a (enter percentage) late payment charge.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm text-[#0A0A0A]">
                Set up your payment terms which will show on all invoices not when payment is due so members know their expected payment timeline.
              </p>
              <div
                role="radiogroup"
                aria-label="Invoice payment terms"
                className="flex flex-wrap gap-6"
              >
                {invoiceTermOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex cursor-pointer items-center gap-2 text-sm text-[#0A0A0A]"
                  >
                    <Radio
                      checked={
                        paymentsAndTaxes.invoicePaymentTerms === option.value
                      }
                      onChange={() =>
                        updateInvoicingOptions((current) => ({
                          ...current,
                          invoicePaymentTerms: option.value,
                        }))
                      }
                      sx={inlineChoiceControlSx}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {/* Design (11) 6594-6612: the ENABLED toggle sits in the header row. */}
              <div className="flex items-start justify-between gap-5">
                <div className="flex min-w-0 flex-col gap-1">
                  <h4 className={designSubsectionTitleClassName}>Overdue Invoice</h4>
                  <p className="text-sm text-[#0A0A0A]">
                    Set up your {noun.possessive} late fee rate. If left blank it will simply not be subject to a late payment fee.
                  </p>
                </div>
                <div className="shrink-0">
                  <EnabledSwitchControl
                    checked={paymentsAndTaxes.overdueInvoiceLateFeeEnabled}
                    onChange={(overdueInvoiceLateFeeEnabled) =>
                      updateInvoicingOptions((current) => ({
                        ...current,
                        overdueInvoiceLateFeeEnabled,
                      }))
                    }
                    ariaLabel="Overdue invoice late fee"
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 max-w-[200px] flex-[1_1_90px]">
                  <DecimalNumberInput
                    value={paymentsAndTaxes.overdueInvoiceLateFeeAmountInDollars}
                    onChange={(overdueInvoiceLateFeeAmountInDollars) =>
                      updateInvoicingOptions((current) => ({
                        ...current,
                        overdueInvoiceLateFeeAmountInDollars,
                      }))
                    }
                    placeholder="0.00"
                    ariaLabel="Overdue invoice late fee amount"
                    disabled={!paymentsAndTaxes.overdueInvoiceLateFeeEnabled}
                    className={globalSettingsDesignInputClassName}
                  />
                </div>
                <div className="relative w-[90px]">
                  <DecimalNumberInput
                    value={paymentsAndTaxes.overdueInvoiceLateFeePercentage}
                    onChange={(overdueInvoiceLateFeePercentage) =>
                      updateInvoicingOptions((current) => ({
                        ...current,
                        overdueInvoiceLateFeePercentage,
                      }))
                    }
                    placeholder="0"
                    ariaLabel="Overdue invoice late fee percentage"
                    disabled={!paymentsAndTaxes.overdueInvoiceLateFeeEnabled}
                    className={`${globalSettingsDesignInputClassName} pr-[26px]`}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#0A0A0A]">
                    %
                  </span>
                </div>
              </div>
              {/* Design (24) 8640-8655: Invoice Footer Notes follows the late fee row; the read-only line greys out while DISABLED. */}
              <div className="mt-[6px] flex flex-col gap-[6px]">
                <div className="flex items-start justify-between gap-5">
                  <div className="flex min-w-0 flex-col gap-[6px]">
                    <h4 className={designSubsectionTitleClassName}>Invoice Footer Notes</h4>
                    <p className="text-sm text-[#0A0A0A]">
                      Enable if you want all invoices to display the link to your Cancellation and Refund Policy page on your website. You must ensure you have added the content which will be displayed on this page in your website builder before enabling.
                    </p>
                  </div>
                  <div className="shrink-0">
                    <EnabledSwitchControl
                      checked={paymentsAndTaxes.invoiceFooterNotesEnabled}
                      onChange={(invoiceFooterNotesEnabled) =>
                        updateInvoicingOptions((current) => ({
                          ...current,
                          invoiceFooterNotesEnabled,
                        }))
                      }
                      ariaLabel="Invoice footer notes"
                    />
                  </div>
                </div>
                <textarea
                  ref={invoiceFooterNotesRef}
                  readOnly
                  rows={2}
                  value={invoiceFooterNotesText}
                  aria-label="Invoice footer notes text"
                  className={`w-full cursor-default resize-none rounded-[8px] border border-[#9E9E9E] bg-[#F7F7F8] px-[12px] py-[10px] text-[14px] outline-none ${
                    paymentsAndTaxes.invoiceFooterNotesEnabled
                      ? "text-[#0A0A0A]"
                      : "text-[#9E9E9E]"
                  }`}
                />
              </div>
            </div>

            <InvoiceBillingContactFields
              value={paymentsAndTaxes.invoiceBillingContact}
              onChange={(invoiceBillingContact) =>
                updateInvoicingOptions((current) => ({
                  ...current,
                  invoiceBillingContact,
                }))
              }
            />

            <label className="flex flex-col gap-2">
              <span className="text-base font-semibold text-[#0A0A0A]">Set Email to Show on Invoice</span>
              <span className="text-sm text-[#0A0A0A]">
                For receiving e-transfers, the email address entered below will appear in all invoices under the invoice payment options.
              </span>
              <input type="email" value={paymentsAndTaxes.eTransferEmail}
                onChange={(event) => updateInvoicingOptions((current) => ({ ...current, eTransferEmail: event.target.value }))}
                placeholder="payments@yourchamber.ca" aria-label="Set Email to Show on Invoice"
                aria-invalid={Boolean(eTransferEmailError)}
                aria-describedby={eTransferEmailError ? "etransfer-email-error" : undefined}
                className={globalSettingsDesignInputClassName} />
              {eTransferEmailError && <span id="etransfer-email-error" role="alert" className="text-sm text-red-600">{eTransferEmailError}</span>}
            </label>
          </div>
        </div>
        <SectionSaveReset {...sectionControls.invoicingOptions}
          saveDisabled={Boolean(eTransferEmailError) || invoiceCopyErrors.some(Boolean)}
          errorText={sectionControls.invoicingOptions.errorText || eTransferEmailError || invoiceCopyErrors.find(Boolean)} />
      </ScreenshotSection>

      <ScreenshotSection
        title={`Set Up How Members Communicate with your ${noun.Singular}`}
        description="Check the options you would like to appear on the online membership application for members to select."
      >
        <div className="flex flex-col gap-[10px]">
          {membershipApplicationCommunicationOptionRows.map((option) => (
            <label
              key={option.key}
              className="flex cursor-pointer items-start gap-[10px] text-sm text-[#0A0A0A]"
            >
              <Checkbox
                checked={
                  setup.membershipApplicationCommunicationOptions[option.key]
                }
                onChange={(event) =>
                  updateMembershipApplicationCommunicationOption({
                    enabled: event.target.checked,
                    key: option.key,
                  })
                }
                className="mt-px"
                sx={inlineChoiceControlSx}
              />
              <span>{option.label}</span>
            </label>
          ))}
          <label className="flex cursor-not-allowed items-start gap-[10px] text-sm text-[#9E9E9E]">
            <Checkbox
              disabled
              // Shows the saved value; staff cannot change it until the
              // text-message channel exists.
              checked={setup.membershipApplicationCommunicationOptions.textMessageEnabled}
              onChange={() => undefined}
              inputProps={{ "aria-label": textMessageCommunicationOptionLabel }}
              className="mt-px"
              sx={inlineChoiceControlSx}
            />
            <span>
              {textMessageCommunicationOptionLabel}{" "}
              <span className="ml-1 whitespace-nowrap rounded-[10px] bg-[#E1A731] px-2 py-[2px] text-[11px] font-bold text-white">
                Coming Soon
              </span>
            </span>
          </label>
        </div>
        <SectionSaveReset {...sectionControls.memberCommunication} />
      </ScreenshotSection>

      <EmailDomainSettingsSection
        settings={email}
        onStartSenderDomainVerification={onStartSenderDomainVerification}
        onRefreshSenderDomainStatus={onRefreshSenderDomainStatus}
        onRemoveSenderDomain={onRemoveSenderDomain}
        onSendSenderDomainTestEmail={onSendSenderDomainTestEmail}
        senderDomainActionLoading={senderDomainActionLoading}
        senderDomainRefreshLoading={senderDomainRefreshLoading}
        senderDomainRemoveLoading={senderDomainRemoveLoading}
        senderDomainTestEmailLoading={senderDomainTestEmailLoading}
        senderDomainError={senderDomainError}
        hasUnsavedSettingsChanges={hasUnsavedSettingsChanges}
      />

      {additionalAccountsModalOpen && (
        <SettingsModal
          title="Request Additional Account Allocations"
          description={`Tell us what additional accounts your ${noun.singular} needs. This will open an email addressed to ${STAFF_SUPPORT_EMAIL}.`}
          onClose={() => setAdditionalAccountsModalOpen(false)}
          panelClassName="max-w-2xl"
        >
          <div className="space-y-5">
            <FieldLabel label="Request Details">
              <textarea
                value={additionalAccountsRequest}
                onChange={(event) =>
                  setAdditionalAccountsRequest(event.target.value)
                }
                placeholder="Describe the additional account allocations you would like to add."
                className="min-h-[160px] w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-3 text-base text-gray-900 shadow-sm outline-none transition focus:border-cpaGold focus:ring-2 focus:ring-amber-100"
              />
            </FieldLabel>
            <div className="flex flex-col justify-end gap-3 border-t border-gray-200 pt-5 sm:flex-row">
              <button
                type="button"
                onClick={() => setAdditionalAccountsModalOpen(false)}
                className={`${settingsSecondaryButtonClassName} min-w-[140px]`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={openAdditionalAccountsRequestEmail}
                className={`${settingsPrimaryButtonClassName} min-w-[200px]`}
              >
                Email Support
              </button>
            </div>
          </div>
        </SettingsModal>
      )}

      {chamberGroupInsuranceBillingDraft && (
        <SettingsModal
          title="Chamber Group Insurance"
          description="Enter billing details for invoice creation (No invoice will be sent, payment to be received and applied to invoice only)"
          onClose={closeChamberGroupInsuranceBillingDraft}
          panelClassName="max-w-2xl"
        >
          <div className="space-y-5">
            {Object.keys(chamberGroupInsuranceBillingErrors).length > 0 ? (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                Some required details are missing. Fill in every field marked
                with *, then save.
              </div>
            ) : null}
            <label className="block">
              <span className="text-sm font-bold text-gray-900">
                Company Name <span className="text-red-500">*</span>
              </span>
              <input
                value={chamberGroupInsuranceBillingDraft.companyName}
                onChange={(event) =>
                  updateChamberGroupInsuranceBillingDraft(
                    "companyName",
                    event.target.value
                  )
                }
                placeholder="Enter Company Name"
                aria-invalid={Boolean(
                  chamberGroupInsuranceBillingErrors.companyName
                )}
                className={`${setupSectionInputClassName} mt-2 ${
                  chamberGroupInsuranceBillingErrors.companyName
                    ? "border-red-500"
                    : ""
                }`}
              />
              {chamberGroupInsuranceBillingErrors.companyName ? (
                <span className="mt-1 block text-xs text-red-600">
                  {chamberGroupInsuranceBillingErrors.companyName}
                </span>
              ) : null}
            </label>

            <div>
              <span className="text-sm font-bold text-gray-900">
                Company Address <span className="text-red-500">*</span>
              </span>
              <GoogleAddressTextField
                id="group-insurance-street-address"
                value={chamberGroupInsuranceBillingDraft.streetAddress}
                onValueChange={(value) => updateChamberGroupInsuranceBillingDraft("streetAddress", value)}
                onAddressSelect={(address) => {
                  setChamberGroupInsuranceBillingDraft(current => current ? { ...current,
                    streetAddress: address.streetAddress, addressLine2: address.unit ? `Unit ${address.unit}` : "",
                    city: address.city, province: address.province || address.provinceCode,
                    postalCode: address.postalCode, country: address.country,
                  } : current);
                  setChamberGroupInsuranceBillingErrors(current => {
                    const next = { ...current };
                    for (const key of ["streetAddress", "city", "province", "postalCode", "country"] as const) delete next[key];
                    return next;
                  });
                }}
                placeholder="Street Number and Name"
                error={Boolean(chamberGroupInsuranceBillingErrors.streetAddress)}
                nativeInputProps={{
                  "aria-label": "Company street address",
                  className: `${setupSectionInputClassName} mt-2 ${chamberGroupInsuranceBillingErrors.streetAddress ? "border-red-500" : ""}`,
                }}
              />
              {chamberGroupInsuranceBillingErrors.streetAddress ? (
                <span className="mt-1 block text-xs text-red-600">
                  {chamberGroupInsuranceBillingErrors.streetAddress}
                </span>
              ) : null}
              <input
                value={chamberGroupInsuranceBillingDraft.addressLine2}
                onChange={(event) =>
                  updateChamberGroupInsuranceBillingDraft(
                    "addressLine2",
                    event.target.value
                  )
                }
                placeholder="Address Line 2 (Optional)"
                className={`${setupSectionInputClassName} mt-3`}
              />
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(
                  [
                    { field: "city", placeholder: "City" },
                    {
                      field: "province",
                      placeholder: "Province / Territory",
                    },
                    { field: "postalCode", placeholder: "Postal Code" },
                    { field: "country", placeholder: "Select Country" },
                  ] as const
                ).map(({ field, placeholder }) => (
                  <label key={field} className="block">
                    {field === "country" ? (
                      <CountrySelectField
                        id="group-insurance-country"
                        value={chamberGroupInsuranceBillingDraft.country}
                        onValueChange={(value) =>
                          updateChamberGroupInsuranceBillingDraft("country", value)
                        }
                        error={Boolean(chamberGroupInsuranceBillingErrors.country)}
                        nativeInputProps={{
                          "aria-label": "Company country",
                          className: `${setupSectionInputClassName} ${
                            chamberGroupInsuranceBillingErrors.country
                              ? "border-red-500"
                              : ""
                          }`,
                        }}
                      />
                    ) : (
                    <input
                      value={chamberGroupInsuranceBillingDraft[field]}
                      onChange={(event) =>
                        updateChamberGroupInsuranceBillingDraft(
                          field,
                          event.target.value
                        )
                      }
                      placeholder={placeholder}
                      aria-invalid={Boolean(
                        chamberGroupInsuranceBillingErrors[field]
                      )}
                      className={`${setupSectionInputClassName} ${
                        chamberGroupInsuranceBillingErrors[field]
                          ? "border-red-500"
                          : ""
                      }`}
                    />
                    )}
                    {chamberGroupInsuranceBillingErrors[field] ? (
                      <span className="mt-1 block text-xs text-red-600">
                        {chamberGroupInsuranceBillingErrors[field]}
                      </span>
                    ) : null}
                  </label>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="text-sm font-bold text-gray-900">
                Contact Name <span className="text-red-500">*</span>
              </span>
              <input
                value={chamberGroupInsuranceBillingDraft.contactName}
                onChange={(event) =>
                  updateChamberGroupInsuranceBillingDraft(
                    "contactName",
                    event.target.value
                  )
                }
                placeholder="Enter Contact Name"
                aria-invalid={Boolean(
                  chamberGroupInsuranceBillingErrors.contactName
                )}
                className={`${setupSectionInputClassName} mt-2 ${
                  chamberGroupInsuranceBillingErrors.contactName
                    ? "border-red-500"
                    : ""
                }`}
              />
              {chamberGroupInsuranceBillingErrors.contactName ? (
                <span className="mt-1 block text-xs text-red-600">
                  {chamberGroupInsuranceBillingErrors.contactName}
                </span>
              ) : null}
            </label>

            <label className="block">
              <span className="text-sm font-bold text-gray-900">
                Contact Title
              </span>
              <input
                value={chamberGroupInsuranceBillingDraft.contactTitle}
                onChange={(event) =>
                  updateChamberGroupInsuranceBillingDraft(
                    "contactTitle",
                    event.target.value
                  )
                }
                placeholder="Enter Title"
                className={`${setupSectionInputClassName} mt-2`}
              />
            </label>

            <label className="block">
              <span className="text-sm font-bold text-gray-900">
                Contact Email <span className="text-red-500">*</span>
              </span>
              <input
                value={chamberGroupInsuranceBillingDraft.contactEmail}
                onChange={(event) =>
                  updateChamberGroupInsuranceBillingDraft(
                    "contactEmail",
                    event.target.value
                  )
                }
                placeholder="Enter Contact Email"
                type="email"
                aria-invalid={Boolean(
                  chamberGroupInsuranceBillingErrors.contactEmail
                )}
                className={`${setupSectionInputClassName} mt-2 ${
                  chamberGroupInsuranceBillingErrors.contactEmail
                    ? "border-red-500"
                    : ""
                }`}
              />
              {chamberGroupInsuranceBillingErrors.contactEmail ? (
                <span className="mt-1 block text-xs text-red-600">
                  {chamberGroupInsuranceBillingErrors.contactEmail}
                </span>
              ) : null}
            </label>

            <label className="block">
              <span className="text-sm font-bold text-gray-900">
                Phone Number <span className="text-red-500">*</span>
              </span>
              <input
                value={chamberGroupInsuranceBillingDraft.phoneNumber}
                onChange={(event) =>
                  updateChamberGroupInsuranceBillingDraft(
                    "phoneNumber",
                    formatStaffPhoneInput(event.target.value)
                  )
                }
                placeholder="Enter Phone Number"
                aria-invalid={Boolean(
                  chamberGroupInsuranceBillingErrors.phoneNumber
                )}
                className={`${setupSectionInputClassName} mt-2 ${
                  chamberGroupInsuranceBillingErrors.phoneNumber
                    ? "border-red-500"
                    : ""
                }`}
              />
              {chamberGroupInsuranceBillingErrors.phoneNumber ? (
                <span className="mt-1 block text-xs text-red-600">
                  {chamberGroupInsuranceBillingErrors.phoneNumber}
                </span>
              ) : null}
            </label>

            <div className="flex justify-end gap-3 border-t border-gray-200 pt-5">
              <button
                type="button"
                onClick={closeChamberGroupInsuranceBillingDraft}
                className={settingsSecondaryButtonClassName}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveChamberGroupInsuranceBillingDraft}
                className={settingsPrimaryButtonClassName}
              >
                Save Billing Details
              </button>
            </div>
          </div>
        </SettingsModal>
      )}

      {selectedTemplateMethod && (
        <SettingsModal
          title={`Edit ${selectedTemplateMethod.label} email template`}
          description="Customize the payment instructions members receive when this method is used."
          onClose={() => setTemplateMethodId(null)}
          panelClassName="flex min-h-[75vh] max-h-[85vh] flex-col"
          bodyClassName="flex flex-1 overflow-hidden"
        >
          <div className="flex min-h-0 flex-1 flex-col space-y-4">
            <FieldLabel label="Subject">
              <TextInput
                value={selectedTemplateMethod.emailSubject}
                onChange={(value) =>
                  updatePaymentTemplate(selectedTemplateMethod, "subject", value)
                }
              />
            </FieldLabel>
            <div className="flex min-h-0 flex-1 flex-col">
              <span className="block text-sm font-semibold text-gray-900">
                Email verbiage
              </span>
              <textarea
                value={selectedTemplateMethod.emailBody}
                onChange={(event) =>
                  updatePaymentTemplate(
                    selectedTemplateMethod,
                    "body",
                    event.target.value
                  )
                }
                className="mt-2 min-h-0 flex-1 resize-none rounded-lg border border-gray-300 bg-white px-3 py-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-cpaGold focus:ring-2 focus:ring-amber-100"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setTemplateMethodId(null)}
                className={settingsPrimaryButtonClassName}
              >
                Save Template
              </button>
            </div>
          </div>
        </SettingsModal>
      )}
    </div>
  );
};

export default GlobalSettingsTab;
